import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Badge } from "@workspace/amarmeds-ds/components/ui/badge";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/amarmeds-ds/components/ui/card";
import { Input } from "@workspace/amarmeds-ds/components/ui/input";
import { Label } from "@workspace/amarmeds-ds/components/ui/label";

import { markOrderSeen } from "../alerts";
import { api, ApiError, type OpsOrderDetail } from "../api";
import { dhakaTime, medicineLabel, OPS_CANCEL_REASONS, reasonLabel, STATUS_LABEL, taka } from "../format";
import { RxPhoto } from "../RxPhoto";

// One order as ops sees it: everything about it, the prescription, every event,
// and the two fixes ops makes by phone: cancel it, or correct the address, phone
// or quantities.

const ERRORS: Record<string, string> = {
  cannot_cancel: "This order can no longer be cancelled.",
  order_changed: "Someone else changed this order just now. It has been reloaded.",
  lines_locked: "The pharmacy has already answered, so quantities can no longer change. Cancel instead if needed.",
  contact_locked: "The order is finished; its contact details can no longer change.",
  order_would_be_empty: "An order needs at least one medicine. Cancel it instead.",
  invalid_address: "The address needs 10 to 500 characters.",
  invalid_phone: "That is not a Bangladeshi mobile number.",
  invalid_lines: "Check the quantities (0 to 100 strips).",
};
const explain = (err: unknown) => (err instanceof ApiError ? (ERRORS[err.code] ?? `Could not save (${err.code}).`) : "Could not reach the server.");

export default function OpsOrder({ orderId }: { orderId: string }) {
  const qc = useQueryClient();
  const order = useQuery({ queryKey: ["ops-order", orderId], queryFn: () => api<OpsOrderDetail>(`/ops/orders/${orderId}`), refetchInterval: 15_000 });
  useEffect(() => markOrderSeen(orderId), [orderId]);
  const saved = (data: OpsOrderDetail) => {
    qc.setQueryData(["ops-order", orderId], data);
    void qc.invalidateQueries({ queryKey: ["ops-orders"] });
  };

  if (order.isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (!order.data) return <p className="text-destructive">Order not found.</p>;
  const o = order.data;
  const lateCode = o.deliveryCodeTries >= 5;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Link href="/ops/orders">
          <Button size="sm" variant="outline">
            All orders
          </Button>
        </Link>
        <h1 className="text-xl font-semibold">Order {o.orderId}</h1>
        <Badge variant={o.status === "cancelled" || o.status === "rejected" || lateCode ? "destructive" : "secondary"}>{STATUS_LABEL[o.status] ?? o.status}</Badge>
        {o.reason && <span className="text-sm text-muted-foreground">{reasonLabel(o.reason)}</span>}
        <Link href={`/queue/${o.orderId}`} className="ml-auto">
          <Button size="sm" variant="ghost">
            Pharmacy view
          </Button>
        </Link>
      </div>
      {lateCode && <p className="text-sm text-destructive">The delivery code is locked after 5 wrong tries. Call the customer.</p>}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row k="Pharmacy" v={`${o.pharmacy.name} (#${o.pharmacy.id})`} />
            <Row k="Area" v={[o.area, o.zone].filter(Boolean).join(" · ")} />
            <Row k="Placed" v={dhakaTime(o.placedAt)} />
            <Row k="Deliver by" v={dhakaTime(o.deliverBy)} />
            {o.deliveredAt && <Row k="Delivered" v={dhakaTime(o.deliveredAt)} />}
            <Row k="Payment" v={o.paymentMethod === "cod" ? "Cash on delivery" : "Online"} />
            {o.paymentMethod === "online" && <Row k="Paid / refunded" v={`${taka(o.payment.paidPaisa)} / ${taka(o.payment.refundPaisa)}`} />}
            <Row k="Collect at door" v={taka(o.payment.collectOnDeliveryPaisa)} />
            {o.payment.cashCollectedPaisa !== null && <Row k="Cash collected" v={taka(o.payment.cashCollectedPaisa)} />}
            <Row k="Run" v={o.run ? `Run ${o.run.runId} · ${o.run.riderName} · ${o.run.status}` : "—"} />
          </CardContent>
        </Card>

        <ContactCard order={o} onSaved={saved} />
      </div>

      <LinesCard order={o} onSaved={saved} />

      {o.hasPrescription && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Prescription</CardTitle>
          </CardHeader>
          <CardContent>
            <RxPhoto orderId={o.orderId} height={480} />
          </CardContent>
        </Card>
      )}

      {o.canCancel && <CancelCard order={o} onSaved={saved} />}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">History</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm">
            {o.events.map((e, i) => (
              <li key={i} className="flex flex-wrap gap-x-3 border-b pb-2 last:border-0">
                <span className="w-32 shrink-0 text-muted-foreground">{dhakaTime(e.at)}</span>
                <span className="font-medium">{e.from === e.to ? "Edited" : (STATUS_LABEL[e.to] ?? e.to)}</span>
                <span className="text-muted-foreground">by {e.actor}</span>
                {e.note && <span className="w-full text-xs text-muted-foreground sm:w-auto">{e.note}</span>}
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-3">
      <span className="w-32 shrink-0 text-muted-foreground">{k}</span>
      <span>{v || "—"}</span>
    </div>
  );
}

function ContactCard({ order: o, onSaved }: { order: OpsOrderDetail; onSaved: (d: OpsOrderDetail) => void }) {
  const [editing, setEditing] = useState(false);
  const [address, setAddress] = useState(o.contact?.address ?? "");
  const [phone, setPhone] = useState(o.contact?.phone ?? "");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api<OpsOrderDetail>(`/ops/orders/${o.orderId}`, { method: "PATCH", body: { address, phone, note } }),
    onSuccess: (d) => {
      onSaved(d);
      setEditing(false);
      setNote("");
      setError(null);
    },
    onError: (err) => setError(explain(err)),
  });
  const start = () => {
    setAddress(o.contact?.address ?? "");
    setPhone(o.contact?.phone ?? "");
    setError(null);
    setEditing(true);
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Customer</CardTitle>
        {o.canEditContact && !editing && (
          <Button size="sm" variant="outline" onClick={start}>
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!o.contact && <p className="text-muted-foreground">Address and phone were deleted 14 days after the order ended.</p>}
        {o.contact && !editing && (
          <>
            <div>{o.contact.address}</div>
            <a href={`tel:${o.contact.phone}`} className="underline">
              {o.contact.phone}
            </a>
          </>
        )}
        {editing && (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="oo-address">Address</Label>
              <textarea id="oo-address" className="min-h-20 w-full rounded-md border bg-background p-2" value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="oo-phone">Phone</Label>
              <Input id="oo-phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="oo-note">Why (kept in the history)</Label>
              <Input id="oo-note" placeholder="Customer phoned with the right flat number" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        {error && <p className="text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

function LinesCard({ order: o, onSaved }: { order: OpsOrderDetail; onSaved: (d: OpsOrderDetail) => void }) {
  const [editing, setEditing] = useState(false);
  const [strips, setStrips] = useState<Record<number, string>>({});
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api<OpsOrderDetail>(`/ops/orders/${o.orderId}`, {
        method: "PATCH",
        body: { lines: o.lines.map((l) => ({ lineId: l.lineId, strips: Number(strips[l.lineId] ?? l.strips) })), note },
      }),
    onSuccess: (d) => {
      onSaved(d);
      setEditing(false);
      setNote("");
      setError(null);
    },
    onError: (err) => setError(explain(err)),
  });
  const valid = o.lines.every((l) => /^\d{1,3}$/.test(strips[l.lineId] ?? String(l.strips)) && Number(strips[l.lineId] ?? l.strips) <= 100);
  const start = () => {
    setStrips(Object.fromEntries(o.lines.map((l) => [l.lineId, String(l.strips)])));
    setError(null);
    setEditing(true);
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Medicines</CardTitle>
        {o.canEditLines && !editing && (
          <Button size="sm" variant="outline" onClick={start}>
            Change quantities
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {o.lines.map((l) => (
          <div key={l.lineId} className="flex flex-wrap items-center gap-3 border-b pb-2 last:border-0">
            <div className="min-w-48 flex-1">
              <div className="font-medium">{medicineLabel(l.medicine, l.medicineId)}</div>
              <div className="text-muted-foreground">
                {taka(l.unitPricePaisa)} / strip · {l.lineStatus.replace(/_/g, " ")}
                {l.substitute && ` · substitute ${medicineLabel(l.substitute.medicine, l.substitute.medicineId)}`}
              </div>
            </div>
            {editing ? (
              <label className="flex items-center gap-2">
                <Input
                  className="w-20"
                  inputMode="numeric"
                  value={strips[l.lineId] ?? ""}
                  onChange={(e) => setStrips((s) => ({ ...s, [l.lineId]: e.target.value.replace(/\D/g, "").slice(0, 3) }))}
                />
                strips {strips[l.lineId] === "0" && <span className="text-destructive">(removed)</span>}
              </label>
            ) : (
              <span>
                {l.strips} strip{l.strips === 1 ? "" : "s"}
              </span>
            )}
            <span className="w-20 text-right">{taka(l.linePaisa)}</span>
          </div>
        ))}
        <div className="flex justify-end gap-6 pt-1">
          <span className="text-muted-foreground">Delivery {taka(o.deliveryFeePaisa)}</span>
          <span className="font-semibold">Total {taka(o.totalPaisa)}</span>
        </div>
        {editing && (
          <div className="space-y-2 pt-2">
            <p className="text-muted-foreground">0 removes a medicine. The customer gets a notice with the new total.</p>
            <Input placeholder="Why (kept in the history)" value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="flex gap-2">
              <Button size="sm" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
                Save quantities
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        {!o.canEditLines && !["delivered", "cancelled", "rejected"].includes(o.status) && (
          <p className="text-xs text-muted-foreground">Quantities can change only before the pharmacy answers.</p>
        )}
        {error && <p className="text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

function CancelCard({ order: o, onSaved }: { order: OpsOrderDetail; onSaved: (d: OpsOrderDetail) => void }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const cancel = useMutation({
    mutationFn: () => api<OpsOrderDetail>(`/ops/orders/${o.orderId}/cancel`, { body: { reason, note } }),
    onSuccess: (d) => {
      onSaved(d);
      setError(null);
    },
    onError: (err) => setError(explain(err)),
  });
  const onRun = o.status === "assigned" || o.status === "picked_up";
  const go = () => {
    const warn = o.status === "picked_up" ? " The rider has the bag: tell them to bring it back to the pharmacy." : "";
    if (window.confirm(`Cancel order ${o.orderId}? The customer is told.${warn}`)) cancel.mutate();
  };
  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-base">Cancel this order</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {onRun && <p className="text-muted-foreground">It is on run {o.run?.runId} ({o.run?.riderName}) and will be taken off it.</p>}
        <div className="flex flex-wrap gap-2">
          {OPS_CANCEL_REASONS.map((r) => (
            <Button key={r.code} size="sm" variant={reason === r.code ? "destructive" : "outline"} onClick={() => setReason(r.code)}>
              {r.label}
            </Button>
          ))}
        </div>
        <Input placeholder="Note (optional, kept in the history)" value={note} onChange={(e) => setNote(e.target.value)} />
        <Button variant="destructive" disabled={!reason || cancel.isPending} onClick={go}>
          Cancel order
        </Button>
        {error && <p className="text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
